import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LayoutGrid, Users, Store as StoreIcon, BadgeCheck, ShieldHalf, ShoppingBag, Package, ClipboardList,
  Star, LayoutList, Truck, Bike, Settings2, CreditCard, HandCoins, Banknote, QrCode, Send, Undo2, Handshake, Trash2, Plus,
  Percent, Crown, Megaphone, Flag, Scale, MessageSquareWarning, LifeBuoy, BarChart3, KeyRound, ScrollText, Settings, Gift,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import api, { formatApiError } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import { KpiCard } from '../../components/commerce/KpiCard';
import { StatusBadge } from '../../components/commerce/StatusBadge';
import { EmptyState } from '../../components/commerce/EmptyState';
import { fmtRM2, timeAgo, PLANS } from '../../data/mockData';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Stub, ArrowUpIcon, ArrowDownIcon } from './common';

export function CashReconciliation() {
  const { cashEntries, cashAction } = useApp();
  return (
    <div data-testid="admin-cash">
      <h2 className="mb-1 text-base font-semibold text-kelate-ink">Cash Reconciliation</h2>
      <p className="mb-4 text-xs text-kelate-soft">Track cash collected by delivery staff until it is handed over and reconciled by finance.</p>
      {cashEntries.length === 0 && <EmptyState icon={Banknote} title="No cash in the field" description="Cash collections will appear here when riders collect payment." />}
      <div className="space-y-2">
        {cashEntries.map((c) => (
          <div key={c.id} data-testid={`cash-entry-${c.orderNumber}`} className="flex flex-wrap items-center justify-between gap-2 rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] px-4 py-3 text-sm">
            <div>
              <span className="font-medium text-kelate-ink">{c.riderName}</span>
              <span className="ml-2 text-xs text-kelate-soft">Order #{c.orderNumber} · {timeAgo(c.collectedAt)}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-kelate-soft">Collected <b className="text-kelate-darkwood">{fmtRM2(c.amount)}</b>{c.submittedAmount != null && <> · Submitted <b className="text-kelate-darkwood">{fmtRM2(c.submittedAmount)}</b> · Diff <b className={c.submittedAmount - c.amount === 0 ? 'text-kelate-sage' : 'text-kelate-red'}>{fmtRM2(c.submittedAmount - c.amount)}</b></>}</span>
              <StatusBadge status={c.status} />
              {c.status === 'AWAITING_HANDOVER' && <button data-testid={`cash-handover-${c.orderNumber}`} onClick={() => cashAction(c.id, 'handover')} className="rounded-sm bg-kelate-darkwood px-3 py-1.5 text-xs font-medium text-kelate-surface">Confirm Handover</button>}
              {c.status === 'HANDED_TO_FINANCE' && (
                <>
                  <button data-testid={`cash-reconcile-${c.orderNumber}`} onClick={() => cashAction(c.id, 'reconcile')} className="rounded-sm bg-kelate-darkwood px-3 py-1.5 text-xs font-medium text-kelate-surface">Reconcile</button>
                  <button data-testid={`cash-discrepancy-${c.orderNumber}`} onClick={() => cashAction(c.id, 'discrepancy')} className="rounded-sm border border-kelate-red/40 px-3 py-1.5 text-xs font-medium text-kelate-red">Flag Discrepancy</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DigitalCollections() {
  const { digitalCollections, verifyDigitalCollection } = useApp();
  return (
    <div data-testid="admin-digital-collections">
      <h2 className="mb-1 text-base font-semibold text-kelate-ink">Digital Collections</h2>
      <p className="mb-4 text-xs text-kelate-soft">Verify TNG QR and bank transfer payments collected on delivery.</p>
      {digitalCollections.length === 0 && <EmptyState icon={QrCode} title="No digital collections" description="TNG QR and bank transfer collections will appear here for verification." />}
      <div className="space-y-2">
        {digitalCollections.map((c) => (
          <div key={c.id} data-testid={`digital-entry-${c.orderNumber}`} className="flex flex-wrap items-center justify-between gap-2 rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] px-4 py-3 text-sm">
            <div>
              <span className="font-medium text-kelate-ink">#{c.orderNumber}</span>
              <span className="ml-2 text-xs text-kelate-soft">{c.method === 'TNG_QR' ? 'TNG QR' : 'Bank Transfer'} · Ref {c.reference} · by {c.collectedBy} · {timeAgo(c.collectedAt)}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-semibold text-kelate-darkwood">{fmtRM2(c.amount)}</span>
              <StatusBadge status={c.status} />
              {c.status === 'VERIFICATION_REQUIRED' && <button data-testid={`verify-${c.orderNumber}`} onClick={() => verifyDigitalCollection(c.id)} className="rounded-sm bg-kelate-darkwood px-3 py-1.5 text-xs font-medium text-kelate-surface">Verify</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const PAYOUT_TABS = ['READY', 'PROCESSING', 'PAID', 'ON_HOLD', 'FAILED'];

export function Payouts() {
  const { payouts, payoutAction } = useApp();
  const [tab, setTab] = useState('READY');
  const [payTarget, setPayTarget] = useState(null);
  const [meta, setMeta] = useState({ transferRef: '', transferDate: new Date().toISOString().slice(0, 10), note: '' });
  const rows = payouts.filter((p) => p.status === tab);
  return (
    <div data-testid="admin-payouts">
      <h2 className="mb-4 text-base font-semibold text-kelate-ink">Merchant Payouts</h2>
      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-kelate-line">
        {PAYOUT_TABS.map((t) => {
          const count = payouts.filter((p) => p.status === t).length;
          return (
            <button key={t} data-testid={`payout-tab-${t.toLowerCase().replace(/_/g, '-')}`} onClick={() => setTab(t)} className={`flex items-center gap-1.5 px-3 py-2 text-sm ${tab === t ? 'border-b-2 border-kelate-darkwood font-semibold text-kelate-darkwood' : 'text-kelate-soft'}`}>
              {t.replace('_', ' ')} {count > 0 && <span className="rounded-full bg-secondary px-1.5 text-[10px] font-semibold text-kelate-darkwood">{count}</span>}
            </button>
          );
        })}
      </div>
      {rows.length === 0 && <EmptyState icon={Send} title="No payouts in this state" description="Settlements become payouts when orders are delivered and paid." />}
      <div className="space-y-3">
        {rows.map((p) => (
          <div key={p.id} data-testid={`payout-${p.payoutNumber}`} className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-semibold text-kelate-ink">{p.payoutNumber} <span className="text-xs font-normal text-kelate-soft">· {p.merchantName} · {p.orderNumbers.map((n) => `#${n}`).join(', ')}</span></span>
              <StatusBadge status={p.status} />
            </div>
            <div className="mt-2 grid gap-1 text-xs text-kelate-soft sm:grid-cols-3">
              <span>Gross: <b className="text-kelate-ink">{fmtRM2(p.gross)}</b></span>
              <span>Commission: <b className="text-kelate-red">-{fmtRM2(p.commission)}</b></span>
              <span>Net to merchant: <b className="text-kelate-darkwood">{fmtRM2(p.net)}</b></span>
              <span className="sm:col-span-3">Bank: {p.bank.bank} · <span className="font-mono">{p.bank.masked}</span> · {p.bank.holder}</span>
              {p.status === 'PAID' && p.transferRef && (
                <span className="sm:col-span-3" data-testid={`payout-ref-${p.payoutNumber}`}>Transfer ref: <span className="font-mono text-kelate-ink">{p.transferRef}</span> · {p.transferDate}{p.note ? ` · ${p.note}` : ''}</span>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {p.status === 'READY' && (
                <>
                  <button data-testid={`payout-processing-${p.payoutNumber}`} onClick={() => payoutAction(p.id, 'processing')} className="rounded-sm border border-kelate-darkwood px-3 py-1.5 text-xs font-medium text-kelate-darkwood">Mark Processing</button>
                  <button data-testid={`payout-hold-${p.payoutNumber}`} onClick={() => payoutAction(p.id, 'hold')} className="rounded-sm border border-kelate-line px-3 py-1.5 text-xs font-medium text-kelate-soft">Put on Hold</button>
                  <button data-testid={`payout-paid-${p.payoutNumber}`} onClick={() => { setPayTarget(p); setMeta({ transferRef: '', transferDate: new Date().toISOString().slice(0, 10), note: '' }); }} className="rounded-sm bg-kelate-darkwood px-3 py-1.5 text-xs font-medium text-kelate-surface">Mark Paid</button>
                </>
              )}
              {p.status === 'PROCESSING' && <button data-testid={`payout-paid-${p.payoutNumber}`} onClick={() => { setPayTarget(p); setMeta({ transferRef: '', transferDate: new Date().toISOString().slice(0, 10), note: '' }); }} className="rounded-sm bg-kelate-darkwood px-3 py-1.5 text-xs font-medium text-kelate-surface">Mark Paid</button>}
              {p.status === 'ON_HOLD' && <button data-testid={`payout-release-${p.payoutNumber}`} onClick={() => payoutAction(p.id, 'ready')} className="rounded-sm border border-kelate-darkwood px-3 py-1.5 text-xs font-medium text-kelate-darkwood">Release to Ready</button>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!payTarget} onOpenChange={() => setPayTarget(null)}>
        <DialogContent className="bg-kelate-surface border-kelate-line max-w-sm" data-testid="mark-paid-modal">
          <DialogHeader>
            <DialogTitle className="text-kelate-ink">Mark {payTarget?.payoutNumber} as Paid</DialogTitle>
            <DialogDescription className="text-kelate-soft">High-risk action — requires transfer details and is recorded in the audit log. Merchant will be notified with a sound alert.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input data-testid="transfer-ref" placeholder="Transfer reference (e.g. PB-TXN-12345)" value={meta.transferRef} onChange={(e) => setMeta({ ...meta, transferRef: e.target.value })} className="h-10 w-full rounded-sm border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood" />
            <input data-testid="transfer-date" type="date" value={meta.transferDate} onChange={(e) => setMeta({ ...meta, transferDate: e.target.value })} className="h-10 w-full rounded-sm border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood" />
            <input data-testid="transfer-note" placeholder="Internal note" value={meta.note} onChange={(e) => setMeta({ ...meta, note: e.target.value })} className="h-10 w-full rounded-sm border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood" />
            <button
              data-testid="confirm-mark-paid"
              disabled={!meta.transferRef}
              onClick={() => {
                if (!meta.transferRef) return toast.error('Transfer reference is required');
                payoutAction(payTarget.id, 'paid', meta);
                setPayTarget(null);
              }}
              className="w-full rounded-sm bg-kelate-darkwood py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood disabled:cursor-not-allowed disabled:opacity-40"
            >
              Confirm Payment of {payTarget ? fmtRM2(payTarget.net) : ''}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

